import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '../services/api';
import axios from 'axios';
import HSCodeSearchWithUoM from '../components/HSCodeSearchWithUoM';
import BuyerModal from '../components/BuyerModal';
import { useAuth } from '../contexts/AuthContext';
import { 
    removeDuplicateScenarios, 
    debugScenarios
} from '../utils/scenarioUtils';

const CanvasInvoiceForm = () => {
    const navigate = useNavigate();
    const { user } = useAuth(); // Get user data from AuthContext
    const [loading, setLoading] = useState(false);
    const [scenarios, setScenarios] = useState([]);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [generatedJson, setGeneratedJson] = useState(null);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [, setHsCodeOptions] = useState([]);
    const [uomOptions, setUomOptions] = useState([]);
    const [, setRateOptions] = useState([]);

    // FBR Validation and Submission state
    const [validationResult, setValidationResult] = useState(null);
    const [licenseStatus, setLicenseStatus] = useState(null);
    const [error, setError] = useState(null);

    // AGGRESSIVE ENDPOINT CONFIRMATION STATE
    const [, setShowEndpointConfirmation] = useState(false);
    const [, setEndpointInfo] = useState(null);

    // Buyer Management state
    const [showBuyerPopup, setShowBuyerPopup] = useState(false);
    const [, setSavedBuyers] = useState([]);
    const [, setFilteredBuyers] = useState([]);
    const [buyerSearchTerm, setBuyerSearchTerm] = useState('');
    const [, setBuyersLoading] = useState(false);
    const [applyFurtherTax, setApplyFurtherTax] = useState(false);

    // Reference data state
    const [referenceData, setReferenceData] = useState({});
    
    // HS Code lookup state
    const [, setHsCodeLoading] = useState({});
    const [hsCodeResults, setHsCodeResults] = useState({});
    
    // Pending operations state
    const [, setPendingOperation] = useState(null);
    const [, setSubmissionResult] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        scenarioId: '',
        invoiceType: 'Sale Invoice',
        invoiceDate: new Date().toISOString().split('T')[0],
        sellerNTNCNIC: '',
        sellerBusinessName: '',
        sellerProvince: '',
        sellerAddress: '',
        buyerNTNCNIC: '9999999',
        buyerBusinessName: 'Walk in Customer',
        buyerProvince: 'Punjab',
        buyerAddress: 'Walk In Customer Address',
        buyerRegistrationType: 'Unregistered',
        invoiceRefNo: '',
        reason: '',
        items: [{
            hsCode: '',
            productDescription: '',
            rate: '',
            taxRateText: '',
            uoM: 'NOS',
            quantity: '',
            totalValues: '',
            valueSalesExcludingST: '',
            fixedNotifiedValueOrRetailPrice: '',
            salesTaxApplicable: '',
            salesTaxWithheldAtSource: '0',
            extraTax: '0',
            furtherTax: '0',
            sroScheduleNo: '',
            fedPayable: '0',
            discount: '0',
            saleType: '',
            sroItemSerialNo: '',
            unitPriceExcludingTax: '',
            singleUnitPrice: '',
            taxCalculationType: 'exclusive'
        }]
    });

    const provinces = [
        'Punjab', 'Sindh', 'Khyber Pakhtunkhwa', 'Balochistan',
        'Gilgit-Baltistan', 'Azad Jammu and Kashmir', 'Islamabad Capital Territory'
    ];

    const registrationTypes = [
        'Registered', 'Unregistered'
    ];

    // Debounce timer ref
    const debounceTimerRef = useRef(null);

    // Calculate item values based on formulas
    const calculateItemValues = useCallback((item, scenarioId) => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.singleUnitPrice) || 0;
        const taxRate = parseFloat(item.rate) || 0;
        const discount = parseFloat(item.discount) || 0;
        const salesTaxWithheld = parseFloat(item.salesTaxWithheldAtSource) || 0;
        const extraTax = parseFloat(item.extraTax) || 0;
        const furtherTax = parseFloat(item.furtherTax) || 0;
        const fedPayable = parseFloat(item.fedPayable) || 0;
        
        // Find scenario for specific calculations
        let valueSalesExcludingST = 0;
        let salesTaxApplicable = 0;
        let fixedNotifiedValueOrRetailPrice = 0;
        
        // Base calculation
        const baseValue = quantity * unitPrice;
        
        // Scenario-specific calculations
        if (scenarioId === 'SN008' || scenarioId === 'SN027') {
            // SN008 & SN027: 3rd Schedule Goods - Fixed retail price scenarios
            console.log(`🧮 Calculating values for ${scenarioId} (3rd Schedule Goods) based on correct formula`);

            // For SN008/SN027, singleUnitPrice IS the MRP (Maximum Retail Price)
            const mrp = unitPrice > 0 ? unitPrice : (parseFloat(item.fixedNotifiedValueOrRetailPrice) || 0);

            console.log(`📊 ${scenarioId} Calculation inputs:`, {
                singleUnitPrice: unitPrice,
                mrp: mrp,
                quantity,
                taxRate
            });

            // ✅ CORRECT FORMULA: fixedNotifiedValueOrRetailPrice = (quantity * singleUnitPrice) / (1 + taxRate / 100)
            fixedNotifiedValueOrRetailPrice = (quantity * mrp) / (1 + taxRate / 100);

            // Calculate sales tax based on the fixed notified value
            salesTaxApplicable = fixedNotifiedValueOrRetailPrice * (taxRate / 100);

            // For SN008/SN027: valueSalesExcludingST should be equal to fixedNotifiedValueOrRetailPrice
            valueSalesExcludingST = fixedNotifiedValueOrRetailPrice;

            console.log(`📊 ${scenarioId} Calculation results:`, {
                fixedNotifiedValueOrRetailPrice: fixedNotifiedValueOrRetailPrice,
                salesTaxApplicable: salesTaxApplicable,
                formula: `(${quantity} * ${mrp}) / (1 + ${taxRate}/100) = ${fixedNotifiedValueOrRetailPrice}`
            });
            
            // Total Values calculation for SN008/SN027 (3rd Schedule Goods)
            // ✅ UNIFIED FORMULA: totalValues = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheldAtSource)
            const totalValues = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheld);

            // Unit Price Excluding Tax = fixedNotifiedValueOrRetailPrice / quantity
            const unitPriceExcludingTax = fixedNotifiedValueOrRetailPrice / quantity;

            return {
                ...item,
                totalValues: Math.round(totalValues * 100) / 100,
                valueSalesExcludingST: Math.round(valueSalesExcludingST * 100) / 100,
                fixedNotifiedValueOrRetailPrice: Math.round(fixedNotifiedValueOrRetailPrice * 100) / 100,
                salesTaxApplicable: Math.round(salesTaxApplicable * 100) / 100,
                unitPriceExcludingTax: Math.round(unitPriceExcludingTax * 100) / 100,
                singleUnitPrice: mrp // Ensure singleUnitPrice reflects the MRP
            };
        } else {
            // Standard calculation for all other scenarios
            if (item.priceIncludesTax) {
                // Tax inclusive calculation
                valueSalesExcludingST = baseValue / (1 + (taxRate / 100));
                salesTaxApplicable = baseValue - valueSalesExcludingST;
                fixedNotifiedValueOrRetailPrice = 0; // Always 0 for standard scenarios
            } else {
                // Tax exclusive calculation
                valueSalesExcludingST = baseValue;
                salesTaxApplicable = (valueSalesExcludingST * taxRate) / 100;
                fixedNotifiedValueOrRetailPrice = 0; // Always 0 for standard scenarios
            }
            
            // Total Values calculation for standard scenarios
            const totalValues = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheld);
            
            return {
                ...item,
                totalValues: Math.round(totalValues * 100) / 100,
                valueSalesExcludingST: Math.round(valueSalesExcludingST * 100) / 100,
                fixedNotifiedValueOrRetailPrice: Math.round(fixedNotifiedValueOrRetailPrice * 100) / 100,
                salesTaxApplicable: Math.round(salesTaxApplicable * 100) / 100,
                unitPriceExcludingTax: baseValue > 0 ? Math.round(((baseValue - discount) / quantity) * 100) / 100 : 0
            };
        }
    }, []);

    // Debounced calculation function with automatic further tax recalculation
    const debouncedCalculateItemValues = useCallback((itemIndex, updatedItem, scenarioId) => {
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            const calculatedItem = calculateItemValues(updatedItem, scenarioId);
            
            setFormData(prev => {
                let finalItem = calculatedItem;
                
                // If applyFurtherTax is true and NOT SN008/SN027, recalculate 4% further tax based on new valueSalesExcludingST
                const isSN008orSN027 = ['SN008', 'SN027'].includes(scenarioId);
                if (applyFurtherTax && !isSN008orSN027) {
                    const valueSalesExcludingST = parseFloat(finalItem.valueSalesExcludingST || 0);
                    const furtherTax = Math.round((valueSalesExcludingST * 0.04) * 100) / 100;
                    
                    // Recalculate totalValues with updated furtherTax
                    const salesTaxApplicable = parseFloat(finalItem.salesTaxApplicable || 0);
                    const extraTax = parseFloat(finalItem.extraTax || 0);
                    const fedPayable = parseFloat(finalItem.fedPayable || 0);
                    const discount = parseFloat(finalItem.discount || 0);
                    const salesTaxWithheldAtSource = parseFloat(finalItem.salesTaxWithheldAtSource || 0);
                    
                    // ✅ UNIFIED FORMULA: totalValues = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheldAtSource)
                    const totalValues = Math.round((valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheldAtSource)) * 100) / 100;
                    
                    console.log(`💰 Updated furtherTax (4% of ${valueSalesExcludingST}): ${furtherTax}, totalValues: ${totalValues}`);
                    
                    finalItem = {
                        ...finalItem,
                        furtherTax,
                        totalValues
                    };
                }
                
                return {
                    ...prev,
                    items: prev.items.map((item, i) => 
                        i === itemIndex ? finalItem : item
                    )
                };
            });
        }, 300); // 300ms debounce delay
    }, [calculateItemValues, applyFurtherTax]);

    // Auto-set applyFurtherTax based on buyerRegistrationType
    // NOTE: SN008 and SN027 should never have 4% furtherTax applied automatically
    useEffect(() => {
        const isSN008orSN027 = ['SN008', 'SN027'].includes(formData.scenarioId);
        const shouldApplyFurtherTax = formData.buyerRegistrationType === 'Unregistered' && !isSN008orSN027;
        setApplyFurtherTax(shouldApplyFurtherTax);
        console.log(`🏷️ Buyer Type: ${formData.buyerRegistrationType}, Scenario: ${formData.scenarioId}, Apply Further Tax: ${shouldApplyFurtherTax}`);
    }, [formData.buyerRegistrationType, formData.scenarioId]);

    // Auto-apply/update 4% further tax when applyFurtherTax changes or when valueSalesExcludingST changes
    // NOTE: SN008 and SN027 should never have 4% furtherTax applied
    useEffect(() => {
        if (applyFurtherTax) {
            const isSN008orSN027 = ['SN008', 'SN027'].includes(formData.scenarioId);
            if (isSN008orSN027) {
                console.log('⚠️ Skipping 4% furtherTax for SN008/SN027 (not applicable)');
                return;
            }
            console.log('💰 Auto-applying 4% further tax for unregistered buyer...');
            setFormData(prev => {
                const updatedFormData = {
                    ...prev,
                    items: prev.items.map(item => {
                        const valueSalesExcludingST = parseFloat(item.valueSalesExcludingST || 0);
                        const furtherTax = Math.round((valueSalesExcludingST * 0.04) * 100) / 100;
                        
                        // Recalculate totalValues with new furtherTax
                        const valueSalesExcludingST_val = parseFloat(item.valueSalesExcludingST || 0);
                        const salesTaxApplicable_val = parseFloat(item.salesTaxApplicable || 0);
                        const extraTax_val = parseFloat(item.extraTax || 0);
                        const fedPayable_val = parseFloat(item.fedPayable || 0);
                        const discount_val = parseFloat(item.discount || 0);
                        const salesTaxWithheldAtSource_val = parseFloat(item.salesTaxWithheldAtSource || 0);
                        
                        // ✅ UNIFIED FORMULA: totalValues = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheldAtSource)
                        const totalValues = Math.round((valueSalesExcludingST_val + salesTaxApplicable_val + furtherTax + extraTax_val + fedPayable_val - (discount_val + salesTaxWithheldAtSource_val)) * 100) / 100;
                        
                        console.log(`📊 Item calculation: valueSalesExcludingST=${valueSalesExcludingST_val}, salesTax=${salesTaxApplicable_val}, furtherTax=${furtherTax}, extraTax=${extraTax_val}, fedPayable=${fedPayable_val}, discount=${discount_val}, salesTaxWithheldAtSource=${salesTaxWithheldAtSource_val}, totalValues=${totalValues}`);
                        
                        return {
                            ...item,
                            furtherTax,
                            totalValues
                        };
                    })
                };
                console.log('📊 Updated items with 4% further tax:', updatedFormData.items);
                return updatedFormData;
            });
        }
    }, [applyFurtherTax, formData.scenarioId]);

    // Load license status for FBR validation/submission
    const loadBuyers = useCallback(async () => {
        try {
            const response = await api.get('/customers');
            setSavedBuyers(response.data.data || []);
            setFilteredBuyers(response.data.data || []);
        } catch (error) {
            console.error('Error loading buyers:', error);
        } finally {
            setBuyersLoading(false);
        }
    }, []);

    const loadLicenseStatus = async () => {
        try {
            console.log('🔍 Loading license status...');
            const response = await api.get('/auth/license-status');
            console.log('📊 License status response:', response.data);
            setLicenseStatus(response.data);
        } catch (error) {
            console.error('❌ Failed to load license status:', error);
            // Provide more detailed error information
            const errorMessage = error.response?.data?.message || error.message || 'Unable to load license status';
            setLicenseStatus({
                hasLicense: false,
                hasFBRKeys: false,
                canSubmitInvoices: false,
                isLicenseExpired: false,
                message: `License status check failed: ${errorMessage}`,
                error: true
            });
        }
    };

    const loadInitialData = useCallback(async () => {
        try {
            setLoading(true);
            console.log('🔄 Loading initial data...');

            const [scenariosRes, hsCodesRes, uomRes, rateRes, refDataRes] = await Promise.all([
                api.get('/scenarios'),
                api.get('/reference-data/hs-codes'),
                api.get('/reference-data/uom'),
                api.get('/reference-data/rates'),
                api.get('/reference-data')
            ]);

            // Load license status
            await loadLicenseStatus();

            console.log('📊 Raw scenarios response:', scenariosRes.data);

            // Process scenarios
            let processedScenarios = [];
            if (scenariosRes.data?.scenarios) {
                processedScenarios = removeDuplicateScenarios(scenariosRes.data.scenarios);
            } else if (Array.isArray(scenariosRes.data)) {
                processedScenarios = removeDuplicateScenarios(scenariosRes.data);
            }

            console.log('✅ Processed scenarios:', processedScenarios.length);
            debugScenarios(processedScenarios);

            setScenarios(processedScenarios);
            setHsCodeOptions(hsCodesRes.data?.hsCodes || []);
            setUomOptions(uomRes.data?.uom || []);
            setRateOptions(rateRes.data?.rates || []);
            const refData = refDataRes.data?.data || {};
            console.log('📊 Reference Data loaded:', refData);
            console.log('🔧 UoM data:', refData.uom);
            setReferenceData(refData);

            // Load buyers from customers table
            loadBuyers();

            // Note: Seller info is now handled by getSellerInfo() function with AuthContext

            // Buyers will be loaded by loadBuyers() function above

        } catch (error) {
            console.error('❌ Error loading initial data:', error);
            toast.error('Failed to load initial data');
        } finally {
            setLoading(false);
        }
    }, [loadBuyers]);

    // Load initial data on mount
    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const handleScenarioChange = async (scenarioId) => {
        console.log('🎯 Scenario changed to:', scenarioId);
        
        const scenario = scenarios.find(s => s.id === scenarioId);
        console.log('📊 Found scenario:', scenario);
        console.log('🔍 Scenario fields check:', {
            id: scenario?.id,
            name: scenario?.name,
            sroScheduleNo: scenario?.sroScheduleNo,
            saleType: scenario?.saleType,
            sroItemSerialNo: scenario?.sroItemSerialNo,
            taxRateText: scenario?.taxRateText,
            rate: scenario?.rate,
            allFields: Object.keys(scenario || {})
        });
        
        setSelectedScenario(scenario);
        
        // Update form data with scenario-based fields
        setFormData(prev => ({
            ...prev,
            scenarioId,
            // Update all items with scenario-based fields
            items: prev.items.map(item => {
                const updatedItem = {
                    ...item,
                    sroScheduleNo: scenario?.sroScheduleNo || '',
                    saleType: scenario?.saleType || 'Goods at standard rate (default)',
                    sroItemSerialNo: scenario?.sroItemSerialNo || '',
                    taxRateText: scenario?.taxRateText || '', // Text value, not numeric
                    rate: scenario?.rate || '' // Keep numeric rate for calculations
                };

                // For SN008 and SN027, ensure fixedNotifiedValueOrRetailPrice is set if empty
                if (scenarioId === 'SN008' || scenarioId === 'SN027') {
                    console.log(`🎯 Setting up ${scenarioId} for fixed retail price calculation...`);
                    // Only set fixedNotifiedValueOrRetailPrice if it's empty
                    if (!updatedItem.fixedNotifiedValueOrRetailPrice) {
                        updatedItem.fixedNotifiedValueOrRetailPrice = updatedItem.singleUnitPrice || 0;
                    }
                    // Let calculateItemValues handle the rest
                }

                return updatedItem;
            })
        }));
        
        console.log('✅ Updated items with scenario fields:', {
            sroScheduleNo: scenario?.sroScheduleNo,
            saleType: scenario?.saleType,
            sroItemSerialNo: scenario?.sroItemSerialNo,
            taxRateText: scenario?.taxRateText
        });
    };

    const handleItemChange = (index, field, value) => {
        // Update the form data immediately for UI responsiveness
        setFormData(prev => {
            const updatedItems = prev.items.map((item, i) => {
                if (i === index) {
                    // Special case for manual calculation trigger
                    if (field === 'calculatedUpdate') {
                        return value; // value is the entire updated item
                    }
                    
                    return { ...item, [field]: value };
                }
                return item;
            });
            
            const newFormData = { ...prev, items: updatedItems };
            
            // Check if this field should trigger calculation
            const triggerFields = ['quantity', 'singleUnitPrice', 'rate', 'taxCalculationType', 'discount', 'salesTaxWithheldAtSource', 'extraTax', 'furtherTax', 'fedPayable'];
            
            // For SN008/SN027, also trigger on fixedNotifiedValueOrRetailPrice changes
            if (prev.scenarioId === 'SN008' || prev.scenarioId === 'SN027') {
                triggerFields.push('fixedNotifiedValueOrRetailPrice');
            }
            
            if (triggerFields.includes(field)) {
                // Use debounced calculation for numeric fields
                const updatedItem = { ...prev.items[index], [field]: value };
                debouncedCalculateItemValues(index, updatedItem, prev.scenarioId);
            }
            
            return newFormData;
        });
    };

    // UoM code to full description mapping - Updated to match FBR requirements
    const getUoMFullDescription = (uomCode) => {
        const uomMapping = {
            'NOS': 'Numbers, pieces, units',
            'KGS': 'Kilograms',
            'KG': 'Kilograms',  // Add KG mapping
            'LTR': 'Liters',
            'MTR': 'Meters',
            'SQM': 'Square meters',
            'CBM': 'Cubic meters',
            'TON': 'Tons',
            'DOZ': 'Dozens',
            'PCS': 'Pieces',
            'SET': 'Sets',
            'PKT': 'Packets',
            'BOX': 'Boxes',
            'BTL': 'Bottles',
            'BAG': 'Bags',
            'CAN': 'Cans',
            'CTN': 'Cartons',
            'DRM': 'Drums',
            'JAR': 'Jars',
            'PAL': 'Pails',
            'ROL': 'Rolls',
            'SHT': 'Sheets',
            'TUB': 'Tubes',
            // Add more common mappings
            'GM': 'Grams',
            'MT': 'Metric tons',
            'SQFT': 'Square feet',
            'CUFT': 'Cubic feet'
        };
        
        return uomMapping[uomCode] || uomCode || 'Numbers, pieces, units';
    };

    // Default seller information (fallback when user profile is incomplete)
    const getDefaultSellerInfo = () => ({
        sellerNTNCNIC: '0000000000000', // Default NTN for testing
        sellerBusinessName: 'Default Business Name',
        sellerProvince: 'Punjab',
        sellerAddress: 'Default Business Address'
    });

    // Get seller information with proper fallback logic
    const getSellerInfo = () => {
        const defaults = getDefaultSellerInfo();
        
        return {
            sellerNTNCNIC: user?.ntn_cnic || formData.sellerNTNCNIC || defaults.sellerNTNCNIC,
            sellerBusinessName: user?.business_name || formData.sellerBusinessName || defaults.sellerBusinessName,
            sellerProvince: user?.province || formData.sellerProvince || defaults.sellerProvince,
            sellerAddress: user?.address || formData.sellerAddress || defaults.sellerAddress
        };
    };

    // Generate JSON for FBR submission
    const generateInvoiceJson = () => {
        console.log('🔄 Generating invoice JSON...');
        
        // Get seller information with fallbacks
        const sellerInfo = getSellerInfo();
        console.log('🏢 Seller info resolved:', sellerInfo);
        
        // Generate unique invoice reference number
        const timestamp = Date.now();
        const invoiceRefNo = `INV-${formData.scenarioId}-${timestamp}`;
        
        // Format items for JSON - Enhanced with better validation and debugging
        const formattedItems = formData.items.map((item, index) => {
            console.log(`🔍 Formatting item ${index + 1} for JSON display:`, item);
            
            // Ensure rate is properly formatted
            let rateText = item.taxRateText || `${item.rate}%`;
            if (!rateText.includes('%')) {
                rateText = `${rateText}%`;
            }
            
            // Convert UoM to full description
            const fullUoM = getUoMFullDescription(item.uoM);
            console.log(`  UoM conversion: ${item.uoM} → ${fullUoM}`);
            
            const formattedItem = {
                hsCode: item.hsCode || "",
                productDescription: item.productDescription || "",
                rate: rateText,
                uoM: fullUoM,
                quantity: parseFloat(item.quantity) || 1,
                totalValues: parseFloat(item.totalValues) || 0,
                valueSalesExcludingST: parseFloat(item.valueSalesExcludingST) || 0,
                fixedNotifiedValueOrRetailPrice: parseFloat(item.fixedNotifiedValueOrRetailPrice) || 0,
                salesTaxApplicable: parseFloat(item.salesTaxApplicable) || 0,
                salesTaxWithheldAtSource: parseFloat(item.salesTaxWithheldAtSource) || 0,
                extraTax: parseFloat(item.extraTax) || 0,
                furtherTax: parseFloat(item.furtherTax) || 0,
                sroScheduleNo: item.sroScheduleNo || "",
                fedPayable: parseFloat(item.fedPayable) || 0,
                discount: parseFloat(item.discount) || 0,
                saleType: selectedScenario?.saleType || item.saleType || "Goods at standard rate (default)",
                sroItemSerialNo: item.sroItemSerialNo || "",
                singleUnitPrice: parseFloat(item.singleUnitPrice) || 0,
                priceIncludesTax: item.taxCalculationType === 'inclusive' || item.priceIncludesTax || false,
                unitPriceExcludingTax: parseFloat(item.unitPriceExcludingTax) || 0
            };
            
            console.log(`✅ Formatted item ${index + 1} for JSON:`, formattedItem);
            return formattedItem;
        });
        
        // Create the complete invoice JSON
        const invoiceJson = {
            invoiceType: "Sale Invoice",
            scenarioId: formData.scenarioId || "",
            items: formattedItems,
            invoiceDate: formData.invoiceDate || new Date().toISOString().split('T')[0],
            invoiceRefNo: invoiceRefNo,
            // Seller information (with proper fallbacks)
            ...sellerInfo,
            // Buyer information from form
            buyerNTNCNIC: formData.buyerNTNCNIC || "",
            buyerBusinessName: formData.buyerBusinessName || "NA",
            buyerProvince: formData.buyerProvince || "",
            buyerAddress: formData.buyerAddress || "NA",
            buyerRegistrationType: formData.buyerRegistrationType || selectedScenario?.buyerRegistrationType || "Unregistered"
        };

        console.log('✅ Invoice JSON generated successfully');
        setGeneratedJson(invoiceJson);
        setShowJsonModal(true);

        // Return the invoice data for validation/submission
        return invoiceJson;
    };

    // Generate invoice data without showing modal (for validation/submission)
    const generateInvoiceData = () => {
        console.log('🔄 Generating invoice data for validation/submission...');

        // Validate required data
        if (!selectedScenario) {
            console.error('❌ No scenario selected');
            return null;
        }

        if (!formData.items || formData.items.length === 0) {
            console.error('❌ No items in invoice');
            return null;
        }

        // Get seller information with fallbacks
        const sellerInfo = getSellerInfo();
        console.log('🏢 Seller info resolved:', sellerInfo);

        // Generate unique invoice reference number
        // Use user provided invoiceRefNo if available, otherwise generate one
        const timestamp = Date.now();
        const invoiceRefNo = formData.invoiceRefNo || `INV-${formData.scenarioId}-${timestamp}`;

        // Format items for JSON - Enhanced with better validation and debugging
        const formattedItems = formData.items.map((item, index) => {
            console.log(`🔍 Formatting item ${index + 1}:`, item);
            
            // Ensure rate is properly formatted
            let rateText = item.taxRateText || `${item.rate}%`;
            if (!rateText.includes('%')) {
                rateText = `${rateText}%`;
            }
            
            // Convert UoM to full description
            const fullUoM = getUoMFullDescription(item.uoM);
            console.log(`  UoM conversion: ${item.uoM} → ${fullUoM}`);
            
            // Parse numeric values with validation
            const quantity = parseFloat(item.quantity) || 1;
            const valueSalesExcludingST = parseFloat(item.valueSalesExcludingST) || 0;
            const salesTaxApplicable = parseFloat(item.salesTaxApplicable) || 0;
            const furtherTax = parseFloat(item.furtherTax) || 0;
            const extraTax = parseFloat(item.extraTax) || 0;
            const fedPayable = parseFloat(item.fedPayable) || 0;
            const discount = parseFloat(item.discount) || 0;
            const salesTaxWithheldAtSourceVal = parseFloat(item.salesTaxWithheldAtSource) || 0;
            const totalValues = parseFloat(item.totalValues) || 0;
            
            // Validate tax calculation consistency using unified formula
            // ✅ UNIFIED FORMULA: totalValues = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheldAtSource)
            const expectedTotal = valueSalesExcludingST + salesTaxApplicable + furtherTax + extraTax + fedPayable - (discount + salesTaxWithheldAtSourceVal);
            if (Math.abs(totalValues - expectedTotal) > 0.01) {
                console.warn(`⚠️ Item ${index + 1}: Total value calculation mismatch - Expected: ${expectedTotal}, Actual: ${totalValues}`);
            }
            
            const formattedItem = {
                hsCode: item.hsCode || "",
                productDescription: item.productDescription || "",
                rate: rateText,
                uoM: fullUoM,
                quantity: quantity,
                totalValues: totalValues,
                valueSalesExcludingST: valueSalesExcludingST,
                fixedNotifiedValueOrRetailPrice: parseFloat(item.fixedNotifiedValueOrRetailPrice) || 0,
                salesTaxApplicable: salesTaxApplicable,
                salesTaxWithheldAtSource: parseFloat(item.salesTaxWithheldAtSource) || 0,
                extraTax: parseFloat(item.extraTax) || 0,
                furtherTax: parseFloat(item.furtherTax) || 0,
                sroScheduleNo: item.sroScheduleNo || "",
                fedPayable: parseFloat(item.fedPayable) || 0,
                discount: parseFloat(item.discount) || 0,
                saleType: selectedScenario?.saleType || item.saleType || "Goods at standard rate (default)",
                sroItemSerialNo: item.sroItemSerialNo || "",
                singleUnitPrice: parseFloat(item.singleUnitPrice) || 0,
                priceIncludesTax: item.taxCalculationType === 'inclusive' || item.priceIncludesTax || false,
                unitPriceExcludingTax: parseFloat(item.unitPriceExcludingTax) || 0
            };
            
            console.log(`✅ Formatted item ${index + 1}:`, formattedItem);
            return formattedItem;
        });

        // Create the complete invoice JSON
        const invoiceData = {
            invoiceType: formData.invoiceType || "Sale Invoice",
            scenarioId: formData.scenarioId || "",
            reason: formData.reason || "",
            items: formattedItems,
            invoiceDate: formData.invoiceDate || new Date().toISOString().split('T')[0],
            invoiceRefNo: invoiceRefNo,
            // Seller information (with proper fallbacks)
            ...sellerInfo,
            // Buyer information from form
            buyerNTNCNIC: formData.buyerNTNCNIC || "",
            buyerBusinessName: formData.buyerBusinessName || "NA",
            buyerProvince: formData.buyerProvince || "",
            buyerAddress: formData.buyerAddress || "NA",
            buyerRegistrationType: formData.buyerRegistrationType || selectedScenario?.buyerRegistrationType || "Unregistered"
        };

        console.log('✅ Invoice data generated successfully for validation/submission');
        return invoiceData;
    };

    const addItem = () => {
        const newItem = {
            hsCode: '',
            productDescription: '',
            rate: selectedScenario?.rate || '',
            taxRateText: selectedScenario?.taxRateText || '',
            uoM: 'NOS',
            quantity: '',
            totalValues: '',
            valueSalesExcludingST: '',
            fixedNotifiedValueOrRetailPrice: '',
            salesTaxApplicable: '',
            salesTaxWithheldAtSource: '0',
            extraTax: '0',
            furtherTax: '0',
            sroScheduleNo: selectedScenario?.sroScheduleNo || '',
            fedPayable: '0',
            discount: '0',
            saleType: selectedScenario?.saleType || 'Goods at standard rate (default)',
            sroItemSerialNo: selectedScenario?.sroItemSerialNo || '',
            unitPriceExcludingTax: '',
            singleUnitPrice: '',
            taxCalculationType: 'exclusive'
        };

        // For SN008 and SN027, set up for fixed retail price calculation
        if (selectedScenario?.id === 'SN008' || selectedScenario?.id === 'SN027') {
            console.log(`🎯 Adding new item for ${selectedScenario.id} fixed retail price scenario...`);
            // Set a default fixed retail price that user can modify
            newItem.fixedNotifiedValueOrRetailPrice = 0; // User will input this
            // Let calculateItemValues handle the calculations when user inputs values
        }

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            setFormData(prev => ({
                ...prev,
                items: prev.items.filter((_, i) => i !== index)
            }));
        }
    };

    // Buyer management functions
    const selectWalkInCustomer = () => {
        setFormData(prev => ({
            ...prev,
            buyerNTNCNIC: '9999999',
            buyerBusinessName: 'Walk in Customer',
            buyerProvince: 'Punjab',
            buyerAddress: 'Walk In Customer Address',
            buyerRegistrationType: 'Unregistered'
        }));
        toast.success('Walk-in customer selected');
    };

    const clearBuyerFields = () => {
        setFormData(prev => ({
            ...prev,
            buyerNTNCNIC: '',
            buyerBusinessName: '',
            buyerProvince: 'Punjab',
            buyerAddress: '',
            buyerRegistrationType: 'Unregistered'
        }));
        toast.success('Buyer information cleared');
    };

    const selectBuyer = (buyer) => {
        setFormData(prev => ({
            ...prev,
            buyerNTNCNIC: buyer.buyerNTNCNIC || buyer.ntn_cnic || '',
            buyerBusinessName: buyer.buyerBusinessName || buyer.business_name || '',
            buyerProvince: buyer.buyerProvince || buyer.province || 'Punjab',
            buyerAddress: buyer.buyerAddress || buyer.address || '',
            buyerRegistrationType: buyer.buyerRegistrationType || (buyer.registration_type === 'NTN' ? 'Registered' : 'Unregistered') || 'Unregistered'
        }));
        setShowBuyerPopup(false);
        toast.success('Buyer selected successfully');
    };

    // HS Code lookup function
    const lookupHSCode = async (hsCode, itemIndex) => {
        if (!hsCode || hsCode.length < 4) return;

        setHsCodeLoading(prev => ({ ...prev, [itemIndex]: true }));
        
        try {
            let uomFromFBR = null;
            let fbrSuccess = false;

            // Call FBR API to get UoM for HS Code
            try {
                const response = await axios.get('/api/fbr-checker/hs-uom', {
                    params: {
                        hs_code: hsCode,
                        annexure_id: '3' // Default annexure
                    }
                });

                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    const uomData = response.data.data[0];
                    // Use UOM Description from FBR API response
                    uomFromFBR = uomData.uom_description || uomData.uom;
                    fbrSuccess = true;
                    setHsCodeResults(prev => ({ ...prev, [itemIndex]: { 
                        success: true, 
                        uom: uomFromFBR,
                        uom_id: uomData.uom_id,
                        selectedUoM: '', // Will be updated after matching
                        message: `HS Code validated - UOM: ${uomFromFBR}`
                    }}));
                }
            } catch (fbrError) {
                console.warn('FBR API call failed:', fbrError);
                setHsCodeResults(prev => ({ ...prev, [itemIndex]: { error: 'FBR API unavailable' } }));
            }

            // Get product description from reference data (reference_data.description)
            const descriptionRef = referenceData.description?.find(ref => ref.code === hsCode);
            let productDescription = '';

            if (descriptionRef) {
                productDescription = descriptionRef.description;
            }

            // Fallback: Get description from hs_code reference data if description table not available
            if (!productDescription) {
                const hsCodeRef = referenceData.hs_code?.find(ref => ref.code === hsCode);
                if (hsCodeRef) {
                    productDescription = hsCodeRef.description;
                }
            }

            // Get UoM from reference data if not available from FBR
            let referenceUoM = '';
            const uomRef = referenceData.uom?.find(ref => ref.code === hsCode);
            if (uomRef) {
                referenceUoM = uomRef.description;
            }

            // Match FBR UoM with available UoM options
            let selectedUoM = "NOS"; // Default code from reference_data
            
            if (uomFromFBR) {
                // Try to find exact match in UoM options
                const uomOptions = referenceData.uom || [];
                const matchedUoM = uomOptions.find(uom => 
                    uom.description?.toLowerCase().includes(uomFromFBR.toLowerCase()) ||
                    uom.value?.toLowerCase() === uomFromFBR.toLowerCase() ||
                    uom.code?.toLowerCase() === uomFromFBR.toLowerCase()
                );
                
                if (matchedUoM) {
                    selectedUoM = matchedUoM.code; // Use code instead of description
                } else {
                    // If no exact match, try to map common FBR responses to codes
                    const uomMapping = {
                        'numbers, pieces, units': 'NOS',
                        'pieces': 'NOS',
                        'units': 'NOS',
                        'kilograms': 'KG',
                        'kg': 'KG',
                        'grams': 'GM',
                        'liters': 'LTR',
                        'meters': 'MTR',
                        'square meters': 'SQM',
                        'cubic meters': 'CBM'
                    };
                    selectedUoM = uomMapping[uomFromFBR.toLowerCase()] || uomFromFBR;
                }
            } else if (referenceUoM) {
                // Find the code for this UoM description
                const uomRef = referenceData.uom?.find(ref => ref.description === referenceUoM);
                selectedUoM = uomRef?.code || referenceUoM;
            }

            // Update item with all available data
            setFormData(prev => ({
                ...prev,
                items: prev.items.map((item, i) => 
                    i === itemIndex ? { 
                        ...item, 
                        productDescription: productDescription || item.productDescription,
                        uoM: selectedUoM
                    } : item
                )
            }));

            // Update HS Code results with selected UoM
            if (fbrSuccess) {
                setHsCodeResults(prev => ({ 
                    ...prev, 
                    [itemIndex]: { 
                        ...prev[itemIndex],
                        selectedUoM: selectedUoM,
                        message: `HS Code validated - UOM: ${uomFromFBR} → Selected: ${selectedUoM}`
                    }
                }));
            }

            // Show appropriate success message
            if (fbrSuccess && productDescription) {
                toast.success(`✅ HS Code validated with FBR API - UoM: ${selectedUoM}, Description updated`);
            } else if (fbrSuccess) {
                toast.success(`✅ HS Code validated with FBR API - UoM: ${selectedUoM}`);
            } else if (productDescription) {
                toast.success('📝 Product description updated from reference data');
            } else {
                toast.info('⚠️ HS Code entered - please verify manually');
            }

        } catch (error) {
            console.error('HS Code lookup error:', error);
            toast.error('Failed to validate HS Code');
            setHsCodeResults(prev => ({ ...prev, [itemIndex]: { error: error.message } }));
        } finally {
            setHsCodeLoading(prev => ({ ...prev, [itemIndex]: false }));
        }
    };

    const handleHsCodeChange = (index, hsCode) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.map((item, i) => 
                i === index ? { ...item, hsCode } : item
            )
        }));

        // Trigger lookup after a short delay
        if (hsCode && hsCode.includes('.') && hsCode.length >= 8) {
            setTimeout(() => lookupHSCode(hsCode, index), 500);
        }
    };

    // Load buyers when popup opens and clear search when it closes
    useEffect(() => {
        if (showBuyerPopup) {
            loadBuyers();
        } else {
            // Clear search when popup closes
            setBuyerSearchTerm('');
        }
    }, [showBuyerPopup, loadBuyers]);

    // Load buyers with search functionality
    const loadBuyersWithSearch = async (searchTerm = '') => {
        try {
            setBuyersLoading(true);
            const params = searchTerm ? { search: searchTerm } : {};
            const response = await api.get('/customers', { params });
            console.log('🔍 Search API response:', response.data);
            
            if (response.data.data) {
                setSavedBuyers(response.data.data || []);
                setFilteredBuyers(response.data.data || []);
                console.log('✅ Loaded buyers with search:', response.data.data.length);
            }
        } catch (error) {
            console.error('Error searching buyers:', error);
            toast.error('Failed to search buyers');
        } finally {
            setBuyersLoading(false);
        }
    };

    // Search buyers with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (showBuyerPopup) {
                loadBuyersWithSearch(buyerSearchTerm);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [buyerSearchTerm, showBuyerPopup]);

    const handleInvoiceSearch = async () => {
        if (!formData.invoiceRefNo) {
            toast.error('Please enter an Invoice Reference Number');
            return;
        }

        try {
            setLoading(true);
            // Search by FBR Invoice Number
            const response = await api.get('/invoice', {
                params: {
                    fbrInvoiceNumber: formData.invoiceRefNo,
                    limit: 1
                }
            });

            if (response.data.invoices && response.data.invoices.length > 0) {
                const invoice = response.data.invoices[0];
                
                // Check if a Debit Note already exists for this invoice
                // We need to check if there are any other invoices with type 'Debit Note' that reference this invoice
                try {
                    const debitNoteCheck = await api.get('/invoice', {
                        params: {
                            invoiceRefNo: invoice.fbr_invoice_number, // Check if this invoice is referenced
                            invoiceType: 'Debit Note',
                            limit: 1
                        }
                    });
                    
                    if (debitNoteCheck.data.invoices && debitNoteCheck.data.invoices.length > 0) {
                        toast.error('A Debit Note already exists for this invoice');
                        return;
                    }
                } catch (checkError) {
                    console.warn('Failed to check for existing debit notes:', checkError);
                    // Continue anyway as this is a soft check
                }

                // Fetch full invoice details to get items
                const detailResponse = await api.get(`/invoice/${invoice.id}`);
                const fullInvoice = detailResponse.data.invoice;

                // Populate fields
                setFormData(prev => ({
                    ...prev,
                    scenarioId: fullInvoice.scenario_id || fullInvoice.scenarioId || prev.scenarioId, // Ensure scenarioId is updated
                    invoiceRefNo: fullInvoice.fbr_invoice_number || prev.invoiceRefNo, // Ensure invoiceRefNo is set to the found invoice's FBR number
                    buyerNTNCNIC: fullInvoice.buyer_ntn_cnic || '',
                    buyerBusinessName: fullInvoice.buyer_business_name || '',
                    buyerProvince: fullInvoice.buyer_province || '',
                    buyerAddress: fullInvoice.buyer_address || '',
                    buyerRegistrationType: fullInvoice.buyer_registration_type || 'Unregistered',
                    items: (fullInvoice.items || []).map(item => {
                        const scId = fullInvoice.scenario_id || fullInvoice.scenarioId;
                        const qty = parseFloat(item.quantity) || 1;
                        
                        // Get values with fallbacks
                        let valExcl = parseFloat(item.value_sales_excluding_st || item.valueSalesExcludingST || 0);
                        const fixedVal = parseFloat(item.fixed_notified_value_or_retail_price || item.fixedNotifiedValueOrRetailPrice || 0);
                        const salesTax = parseFloat(item.sales_tax_applicable || item.salesTaxApplicable || 0);
                        const totalVal = parseFloat(item.total_values || item.totalValues || 0);
                        
                        // For SN008/SN027, prioritize fixedNotifiedValueOrRetailPrice if valExcl is 0
                        if ((scId === 'SN008' || scId === 'SN027') && fixedVal > 0) {
                            console.log(`🎯 SN008/SN027: Using fixedNotifiedValueOrRetailPrice (${fixedVal}) as valueSalesExcludingST`);
                            valExcl = fixedVal;
                        }
                        
                        let singleUnitPrice = 0;
                        let unitPriceExcludingTax = 0;

                        if (scId === 'SN008' || scId === 'SN027') {
                            // For SN008/SN027, singleUnitPrice is the MRP (Tax Inclusive)
                            if (qty > 0) {
                                if (valExcl > 0) {
                                    singleUnitPrice = (valExcl + salesTax) / qty;
                                } else if (totalVal > 0) {
                                    // Fallback to totalValues if valExcl is missing
                                    singleUnitPrice = totalVal / qty;
                                }
                            }
                            unitPriceExcludingTax = qty > 0 ? valExcl / qty : 0;
                        } else {
                            // Standard scenarios
                            unitPriceExcludingTax = qty > 0 ? valExcl / qty : 0;
                            singleUnitPrice = unitPriceExcludingTax; // Default assumption
                        }

                        console.log(`🔍 Mapped item for ${scId}:`, {
                            qty, valExcl, fixedVal, salesTax, totalVal, singleUnitPrice, unitPriceExcludingTax
                        });

                        return {
                            hsCode: item.hs_code || item.hsCode || '',
                            productDescription: item.product_description || item.productDescription || '',
                            rate: item.rate || '',
                            taxRateText: item.rate || '',
                            uoM: item.uom || item.uoM || 'NOS',
                            quantity: item.quantity || '',
                            totalValues: item.total_values || item.totalValues || '',
                            valueSalesExcludingST: valExcl, // Use the resolved value
                            fixedNotifiedValueOrRetailPrice: fixedVal,
                            salesTaxApplicable: item.sales_tax_applicable || item.salesTaxApplicable || '',
                            salesTaxWithheldAtSource: item.sales_tax_withheld_at_source || item.salesTaxWithheldAtSource || '0',
                            extraTax: item.extra_tax || item.extraTax || '0',
                            furtherTax: item.further_tax || item.furtherTax || '0',
                            sroScheduleNo: item.sro_schedule_no || item.sroScheduleNo || '',
                            fedPayable: item.fed_payable || item.fedPayable || '0',
                            discount: item.discount || '0',
                            saleType: item.sale_type || item.saleType || '',
                            sroItemSerialNo: item.sro_item_serial_no || item.sroItemSerialNo || '',
                            unitPriceExcludingTax: unitPriceExcludingTax, 
                            singleUnitPrice: singleUnitPrice,
                            taxCalculationType: 'exclusive'
                        };
                    })
                }));

                toast.success('Invoice found and details populated');
            } else {
                toast.error('Invoice not found');
            }
        } catch (error) {
            console.error('Error searching invoice:', error);
            toast.error('Error searching invoice');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitInvoice = async () => {
        try {
            setLoading(true);
            // Implementation for invoice submission
            toast.success('Invoice created successfully');
        } catch (error) {
            console.error('Error creating invoice:', error);
            toast.error('Failed to create invoice');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to clean items for FBR payload (rate as text, remove extra fields)
    const cleanItemsForFBR = (items, scenarioId) => (items || []).map((item) => {
        const { salesTaxRate, ratePercentage, unitPriceExcludingTax, ...rest } = item || {};
        return {
            ...rest,
            rate: item.rate || '18%' // Use the rate from the item or default
        };
    });

    // 🔴 AGGRESSIVE: Get endpoint info and show confirmation dialog
    const checkEndpointAndConfirm = async (operation) => {
        try {
            // Fetch user's current environment and endpoint info
            const userResponse = await api.user.getProfile();
            const userEnv = userResponse.data?.profile?.api_environment || 'sandbox';
            const baseURL = 'https://gw.fbr.gov.pk/di_data/v1/di';
            
            let endpoint = '/postinvoicedata_sb'; // Default to sandbox
            if (operation === 'validate') {
                endpoint = userEnv === 'production' ? '/validateinvoicedata' : '/validateinvoicedata_sb';
            } else if (operation === 'submit') {
                endpoint = userEnv === 'production' ? '/postinvoicedata' : '/postinvoicedata_sb';
            }

            const endpointDetails = {
                endpoint,
                environment: userEnv,
                baseURL,
                fullURL: `${baseURL}${endpoint}`,
                operation
            };

            console.log('📡 Endpoint Check:', endpointDetails);

            // Show confirmation dialog
            setEndpointInfo(endpointDetails);
            setPendingOperation(operation);
            setShowEndpointConfirmation(true);

        } catch (err) {
            console.error('❌ Failed to check endpoint:', err);
            toast.error('Failed to check FBR endpoint configuration');
        }
    };

    // 🔴 AGGRESSIVE: Proceed with operation after user confirms endpoint
    const proceedWithConfirmedEndpoint = async (operation) => {
        setShowEndpointConfirmation(false);
        if (operation === 'validate') {
            await performValidateInvoice();
        } else if (operation === 'submit') {
            await performSubmitInvoice();
        }
    };

    void checkEndpointAndConfirm;
    void proceedWithConfirmedEndpoint;

    // Actual validation logic
    const performValidateInvoice = async () => {
        if (!selectedScenario) {
            setError('Please select a scenario first');
            return;
        }

        if (!formData.items || formData.items.length === 0) {
            setError('Please add at least one item to the invoice');
            return;
        }

        // Validate Reason for Debit Note
        if (formData.invoiceType === 'Debit Note' && !formData.reason) {
            setError('Reason is mandatory for Debit Note');
            return;
        }

        // Check license status before validation
        if (!licenseStatus?.canSubmitInvoices) {
            setError('License required: You need an active API Key License and FBR API keys to validate invoices. Please contact administrator or configure your FBR settings.');
            return;
        }

        let cleanedInvoiceData = null;
        try {
            setLoading(true);
            setError(null);

            console.log('🔍 Validating invoice with FBR...');

            // Generate the invoice data
            const invoiceData = generateInvoiceData();
            if (!invoiceData) {
                setError('Failed to generate invoice data');
                return;
            }

            // Clean items for FBR submission
            cleanedInvoiceData = {
                ...invoiceData,
                items: cleanItemsForFBR(invoiceData.items, selectedScenario.id)
            };

            console.log('🔍 Validating invoice data:', cleanedInvoiceData);

            const response = await api.scenarios.validate(selectedScenario.id, {
                invoiceData: cleanedInvoiceData
            });

            console.log('✅ Validation response received:', response.data);
            setValidationResult(response.data);

            // Also show the JSON data that was validated
            setGeneratedJson(cleanedInvoiceData);
            setShowJsonModal(true);

            // Check if validation was successful
            if (response.data.validation?.success) {
                toast.success('Invoice validation successful');
            } else {
                const errorMsg = response.data.validation?.error || response.data.statusMessage || 'Validation failed';
                toast.error(`Validation failed: ${errorMsg}`);
                console.warn('⚠️ Validation failed:', response.data);
            }

        } catch (err) {
            console.error('❌ Validation error:', err);
            
            // Enhanced error handling for different error types
            let errorMessage = 'Failed to validate invoice';
            
            if (err.response?.status === 502) {
                errorMessage = 'FBR API Gateway Error (502) - The FBR service is temporarily unavailable. Please try again later.';
                console.error('🚨 502 Bad Gateway - FBR API issue detected');
            } else if (err.response?.status === 500) {
                errorMessage = 'Server Error (500) - There was an issue processing your request. Please check your data and try again.';
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            // Log the full error for debugging
            console.error('🔍 Full error details:', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                message: err.message,
                invoiceData: cleanedInvoiceData
            });
            
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Validate invoice with FBR - DIRECT CALL
    const validateInvoice = async () => {
        await performValidateInvoice();
    };

    // Submit invoice to FBR - DIRECT CALL
    const submitInvoice = async () => {
        await performSubmitInvoice();
    };

    // Actual submission logic
    const performSubmitInvoice = async () => {
        if (!selectedScenario) {
            setError('Please select a scenario first');
            return;
        }

        if (!formData.items || formData.items.length === 0) {
            setError('Please add at least one item to the invoice');
            return;
        }

        // Validate Reason for Debit Note
        if (formData.invoiceType === 'Debit Note' && !formData.reason) {
            setError('Reason is mandatory for Debit Note');
            return;
        }

        if (!validationResult?.validation?.success) {
            setError('Please validate the invoice first');
            return;
        }

        // Check license status before submission
        if (!licenseStatus?.canSubmitInvoices) {
            setError('License required: You need an active API Key License and FBR API keys to submit invoices. Please contact administrator or configure your FBR settings.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            console.log('🚀 Submitting invoice to FBR...');

            // Generate the invoice data
            const invoiceData = generateInvoiceData();
            if (!invoiceData) {
                setError('Failed to generate invoice data');
                return;
            }

            // Clean items for FBR submission
            const cleanedInvoiceData = {
                ...invoiceData,
                items: cleanItemsForFBR(invoiceData.items, selectedScenario.id)
            };

            console.log('🚀 Submitting invoice data:', cleanedInvoiceData);

            const response = await api.scenarios.submit(selectedScenario.id, {
                invoiceData: cleanedInvoiceData
            });

            setSubmissionResult(response.data);

            // Update form data with FBR-assigned invoice number if available
            if (response.data.result?.data?.submissionResponse?.fbrInvoiceNumber) {
                const fbrInvoiceNumber = response.data.result.data.submissionResponse.fbrInvoiceNumber;
                setFormData(prev => ({
                    ...prev,
                    invoiceRefNo: fbrInvoiceNumber
                }));
                toast.success(`Invoice submitted successfully! FBR Invoice Number: ${fbrInvoiceNumber}`);
            } else if (response.data.fbrInvoiceNumber) {
                setFormData(prev => ({
                    ...prev,
                    invoiceRefNo: response.data.fbrInvoiceNumber
                }));
                toast.success(`Invoice submitted successfully! FBR Invoice Number: ${response.data.fbrInvoiceNumber}`);
            } else {
                toast.success('Invoice submitted to FBR successfully');
            }

        } catch (err) {
            console.error('Submission error:', err);
            setError('Failed to submit invoice: ' + (err.response?.data?.message || err.message));
            toast.error('Failed to submit invoice');
        } finally {
            setLoading(false);
        }
    };

    // Submit invoice to FBR - NOW SHOWS ENDPOINT CONFIRMATION FIRST
    // const submitInvoice = async () => {
    //    await checkEndpointAndConfirm('submit');
    // };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading invoice form...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Create Invoice</h1>
                    <p className="text-gray-600 text-sm">Create a new invoice with automatic tax calculations and FBR integration</p>
                </div>

                {/* License Status Display - Prominent at top */}
                {licenseStatus && (
                    <div className={`mb-6 rounded-lg shadow-lg ${
                        licenseStatus.canSubmitInvoices
                            ? 'bg-green-50 border border-green-200'
                            : licenseStatus.error
                            ? 'bg-red-50 border border-red-200'
                            : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                        <div className="px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className={`h-4 w-4 rounded-full mr-3 ${
                                        licenseStatus.canSubmitInvoices
                                            ? 'bg-green-400'
                                            : licenseStatus.error
                                            ? 'bg-red-400'
                                            : 'bg-yellow-400'
                                    }`}></div>
                                    <div>
                                        <h3 className={`text-base font-semibold ${
                                            licenseStatus.canSubmitInvoices
                                                ? 'text-green-800'
                                                : licenseStatus.error
                                                ? 'text-red-800'
                                                : 'text-yellow-800'
                                        }`}>
                                            {licenseStatus.canSubmitInvoices
                                                ? '✅ Ready for FBR Integration'
                                                : licenseStatus.error
                                                ? '❌ License Status Error'
                                                : '⚠️ FBR Setup Required'}
                                        </h3>
                                        <p className={`text-sm mt-1 ${
                                            licenseStatus.canSubmitInvoices
                                                ? 'text-green-600'
                                                : licenseStatus.error
                                                ? 'text-red-600'
                                                : 'text-yellow-600'
                                        }`}>
                                            {licenseStatus.message}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3 text-sm">
                                    <div className={`px-3 py-1 rounded-full font-medium ${
                                        licenseStatus.hasLicense && !licenseStatus.isLicenseExpired
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        License: {licenseStatus.hasLicense && !licenseStatus.isLicenseExpired ? 'Active' : 'Required'}
                                    </div>
                                    <div className={`px-3 py-1 rounded-full font-medium ${
                                        licenseStatus.hasFBRKeys ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                        FBR Keys: {licenseStatus.hasFBRKeys ? 'Configured' : 'Missing'}
                                    </div>
                                    <div className={`px-3 py-1 rounded-full font-medium ${
                                        (user?.api_environment || 'sandbox') === 'sandbox' 
                                            ? 'bg-yellow-100 text-yellow-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        Environment: {(user?.api_environment || 'sandbox') === 'sandbox' ? 'Sandbox' : 'Production'}
                                    </div>
                                    {licenseStatus.license && (
                                        <div className="text-gray-600 text-sm">
                                            Usage: {licenseStatus.license.current_usage}/{licenseStatus.license.monthly_limit}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {!licenseStatus.canSubmitInvoices && !licenseStatus.error && (
                                <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                                    <p className="text-sm text-yellow-800 font-medium mb-2">To validate and submit invoices to FBR, you need:</p>
                                    <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                                        {!licenseStatus.hasLicense && <li>Active API Key License (contact administrator)</li>}
                                        {licenseStatus.isLicenseExpired && <li>License renewal (contact administrator)</li>}
                                        {!licenseStatus.hasFBRKeys && (
                                            <li>
                                                FBR API keys configured in{' '}
                                                <button
                                                    onClick={() => navigate('/app/profile?tab=fbr-api')}
                                                    className="underline hover:text-yellow-800 font-medium"
                                                >
                                                    Profile Settings
                                                </button>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            )}
                            {licenseStatus.error && (
                                <div className="mt-4 p-3 bg-red-100 rounded-lg">
                                    <p className="text-sm text-red-800">
                                        <strong>Error loading license status.</strong> Please refresh the page or contact administrator if the problem persists.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSubmitInvoice(); }}>
                    {/* 1. Select Scenario */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">1. Select Scenario</h2>
                            <p className="text-sm text-gray-600">Choose the tax scenario for this invoice</p>
                        </div>
                        <div className="px-6 py-4">
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Scenario ID *
                                    </label>
                                    <select
                                        value={formData.scenarioId}
                                        onChange={(e) => handleScenarioChange(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="">Select a scenario...</option>
                                        {scenarios.map(scenario => (
                                            <option key={scenario.id} value={scenario.id}>
                                                {scenario.id} - {scenario.name} ({scenario.rate} tax)
                                            </option>
                                        ))}
                                    </select>
                                    <div className="mt-1 text-xs text-gray-500">
                                        💡 Choose the tax scenario that matches your invoice type
                                    </div>
                                </div>
                                
                                {selectedScenario && (
                                    <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                        <h4 className="font-medium text-blue-900 mb-2">Selected Scenario Details:</h4>
                                        <div className="space-y-2">
                                            <div>
                                                <span className="font-medium text-blue-800">ID:</span> 
                                                <span className="ml-2 text-blue-700">{selectedScenario.id}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-blue-800">Name:</span> 
                                                <span className="ml-2 text-blue-700">{selectedScenario.name}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-blue-800">Description:</span> 
                                                <span className="ml-2 text-blue-700">{selectedScenario.description}</span>
                                            </div>
                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                                <div>
                                                    <span className="font-medium">Tax Rate:</span> {selectedScenario.rate}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Sale Type:</span> {selectedScenario.saleType}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Buyer Type:</span> {selectedScenario.buyerRegistrationType}
                                                </div>
                                            </div>
                                            <div className="mt-2 text-xs text-blue-600">
                                                <span className="font-medium">Formula:</span> {selectedScenario.formula}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 2. Invoice Details */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">2. Invoice Details</h2>
                        </div>
                        <div className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Invoice Type
                                    </label>
                                    <select
                                        value={formData.invoiceType}
                                        onChange={(e) => setFormData(prev => ({ ...prev, invoiceType: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="Sale Invoice">Sale Invoice</option>
                                        <option value="Purchase Invoice">Purchase Invoice</option>
                                        <option value="Debit Note">Debit Note</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Invoice Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.invoiceDate}
                                        onChange={(e) => setFormData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Invoice Reference No.
                                    </label>
                                    <div className="flex space-x-2">
                                        <input
                                            type="text"
                                            value={formData.invoiceRefNo}
                                            onChange={(e) => setFormData(prev => ({ ...prev, invoiceRefNo: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="INV-00001"
                                        />
                                        {formData.invoiceType === 'Debit Note' && (
                                            <button
                                                type="button"
                                                onClick={handleInvoiceSearch}
                                                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                                title="Search Invoice"
                                            >
                                                🔍
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {formData.invoiceType === 'Debit Note' && (
                                    <div className="col-span-1 md:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Reason <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.reason || ''}
                                            onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Reason for Debit Note"
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 3. Buyer Information */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <fieldset disabled={formData.invoiceType === 'Debit Note'} className={formData.invoiceType === 'Debit Note' ? 'opacity-50' : ''}>
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">3. Buyer Information</h2>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    type="button"
                                    onClick={selectWalkInCustomer}
                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                                >
                                    👤 Walk In Customer
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowBuyerPopup(true)}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                >
                                    📋 Manage Buyers
                                </button>
                                <button
                                    type="button"
                                    onClick={clearBuyerFields}
                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                >
                                    🗑️ Clear
                                </button>
                            </div>
                        </div>
                        <div className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Buyer NTN/CNIC
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.buyerNTNCNIC}
                                        onChange={(e) => setFormData(prev => ({ ...prev, buyerNTNCNIC: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter buyer NTN/CNIC"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Buyer Business Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.buyerBusinessName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, buyerBusinessName: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter buyer business name"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Buyer Province
                                    </label>
                                    <select
                                        value={formData.buyerProvince}
                                        onChange={(e) => setFormData(prev => ({ ...prev, buyerProvince: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {referenceData.province?.map(option => (
                                            <option key={option.code} value={option.description}>{option.description}</option>
                                        )) || provinces.map(province => (
                                            <option key={province} value={province}>{province}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Buyer Address
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.buyerAddress}
                                        onChange={(e) => setFormData(prev => ({ ...prev, buyerAddress: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter buyer address"
                                    />
                                </div>
                            </div>
                            <div className="mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Registration Type
                                    </label>
                                    <select
                                        value={formData.buyerRegistrationType}
                                        onChange={(e) => setFormData(prev => ({ ...prev, buyerRegistrationType: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {registrationTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Apply Further Tax Option */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Apply 4% Further Tax for Unregistered Buyer
                                </label>
                                {['SN008', 'SN027'].includes(formData.scenarioId) ? (
                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                        <p className="text-sm text-yellow-800">
                                            ⚠️ Note: 4% further tax is not applicable for SN008 and SN027 scenarios (Fixed Retail Price Goods)
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex items-center space-x-6">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="applyFurtherTax"
                                                value="yes"
                                                checked={applyFurtherTax === true}
                                                onChange={(e) => {
                                                    setApplyFurtherTax(e.target.value === 'yes');
                                                    // Apply 4% further tax to all items
                                                    if (e.target.value === 'yes') {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            items: prev.items.map(item => ({
                                                                ...item,
                                                                furtherTax: Math.round((parseFloat(item.valueSalesExcludingST || 0) * 0.04) * 100) / 100
                                                            }))
                                                        }));
                                                    }
                                                }}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="applyFurtherTax"
                                                value="no"
                                                checked={applyFurtherTax === false}
                                                onChange={(e) => {
                                                    setApplyFurtherTax(e.target.value === 'no' ? false : true);
                                                    // Remove further tax from all items
                                                    if (e.target.value === 'no') {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            items: prev.items.map(item => ({
                                                                ...item,
                                                                furtherTax: '0'
                                                            }))
                                                        }));
                                                    }
                                                }}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">No</span>
                                        </label>
                                    </div>
                                )}
                                <p className="mt-2 text-xs text-gray-500">
                                    {!['SN008', 'SN027'].includes(formData.scenarioId) && (applyFurtherTax ? '✓ 4% further tax will be applied to all items' : 'Further tax is disabled')}
                                </p>
                            </div>
                        </div>
                        </fieldset>
                    </div>

                    {/* 4. Invoice Items */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <fieldset disabled={formData.invoiceType === 'Debit Note'} className={formData.invoiceType === 'Debit Note' ? 'opacity-50' : ''}>
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">4. Invoice Items</h2>
                            </div>
                        </div>
                        <div className="px-6 py-4">
                            {formData.items.map((item, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-md font-medium text-gray-900">Item {index + 1}</h3>
                                        <div className="flex space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => calculateItemValues(index)}
                                                className="px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600"
                                            >
                                                Calculate
                                            </button>
                                            {formData.items.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(index)}
                                                    className="px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Item Details Row 1 */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <HSCodeSearchWithUoM
                                                currentHSCode={item.hsCode}
                                                currentUoM={item.uoM}
                                                onHSCodeSelect={(hsCodeData) => {
                                                    // Update HS Code and Product Description
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((itm, i) => 
                                                            i === index ? { 
                                                                ...itm, 
                                                                hsCode: hsCodeData.code,
                                                                productDescription: hsCodeData.description || itm.productDescription
                                                            } : itm
                                                        )
                                                    }));
                                                    
                                                    // Also trigger FBR validation
                                                    handleHsCodeChange(index, hsCodeData.code);
                                                }}
                                                onUoMChange={(newUoM) => {
                                                    // Update UoM
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((itm, i) => 
                                                            i === index ? { ...itm, uoM: newUoM } : itm
                                                        )
                                                    }));
                                                }}
                                                showUoMValidation={true}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Product Description
                                                <span className="text-xs text-gray-500 ml-2">Auto-populated from HS Code</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={item.productDescription}
                                                onChange={(e) => handleItemChange(index, 'productDescription', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Product Description (auto-filled from HS Code)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Unit of Measure *
                                                <span className="text-xs text-gray-500 ml-2">Auto-suggested from HS Code</span>
                                            </label>
                                            <select
                                                value={item.uoM}
                                                onChange={(e) => handleItemChange(index, 'uoM', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="">Select UoM...</option>
                                                {/* Default option */}
                                                <option value="NOS" className="bg-blue-50">
                                                    NOS - Numbers, pieces, units (Default)
                                                </option>
                                                {/* Show FBR API result first if available */}
                                                {hsCodeResults[index]?.data?.[0]?.uom && (
                                                    <option value={hsCodeResults[index].data[0].uom} className="bg-green-50">
                                                        {hsCodeResults[index].data[0].uom} (From FBR API)
                                                    </option>
                                                )}
                                                {/* Show reference data UoM options - already filtered by type=uom from backend */}
                                                {referenceData.uom?.map((uom, idx) => (
                                                    <option key={`ref-uom-${idx}`} value={uom.code}>
                                                        {uom.code} {uom.description ? `- ${uom.description}` : ''}
                                                    </option>
                                                )) || uomOptions.map((uom, idx) => (
                                                    <option key={`uom-${idx}`} value={uom.code}>
                                                        {uom.code} {uom.description ? `- ${uom.description}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {hsCodeResults[index]?.data?.[0]?.uom && (
                                                <div className="mt-1 text-xs text-green-600">
                                                    💡 UoM auto-filled from FBR API
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Calculated Values Section */}
                                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Calculated Values (auto-calculated via Canvas)</h4>
                                        {/* First Row: User Input Fields */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Quantity *
                                                </label>
                                                <div className="flex items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentQty = parseFloat(item.quantity) || 0;
                                                            if (currentQty > 0) {
                                                                handleItemChange(index, 'quantity', (currentQty - 1).toString());
                                                            }
                                                        }}
                                                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded-l border border-r-0 border-gray-300 hover:bg-gray-300 text-sm"
                                                    >
                                                        −
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={item.quantity || ''}
                                                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                        className="flex-1 px-2 py-1 text-sm border-t border-b border-gray-300 text-center"
                                                        required
                                                        min="0"
                                                        step="1"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentQty = parseFloat(item.quantity) || 0;
                                                            handleItemChange(index, 'quantity', (currentQty + 1).toString());
                                                        }}
                                                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded-r border border-l-0 border-gray-300 hover:bg-gray-300 text-sm"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Unit Price (Excl. Tax) *
                                                </label>
                                                <div className="flex items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentPrice = parseFloat(item.singleUnitPrice) || 0;
                                                            if (currentPrice > 0) {
                                                                handleItemChange(index, 'singleUnitPrice', (currentPrice - 1).toString());
                                                            }
                                                        }}
                                                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded-l border border-r-0 border-gray-300 hover:bg-gray-300 text-sm"
                                                    >
                                                        −
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={item.singleUnitPrice || ''}
                                                        onChange={(e) => handleItemChange(index, 'singleUnitPrice', e.target.value)}
                                                        className="flex-1 px-2 py-1 text-sm border-t border-b border-gray-300 text-center"
                                                        required
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentPrice = parseFloat(item.singleUnitPrice) || 0;
                                                            handleItemChange(index, 'singleUnitPrice', (currentPrice + 1).toString());
                                                        }}
                                                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded-r border border-l-0 border-gray-300 hover:bg-gray-300 text-sm"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Tax Calculation
                                                </label>
                                                <select
                                                    value={item.taxCalculationType || 'exclusive'}
                                                    onChange={(e) => handleItemChange(index, 'taxCalculationType', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                >
                                                    <option value="exclusive">Exclusive</option>
                                                    <option value="inclusive">Inclusive</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Tax Rate
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.taxRateText || `${item.rate || '0'}%`}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                                <div className="text-xs text-gray-500 mt-1">From scenario configuration</div>
                                            </div>
                                        </div>
                                        {/* Second Row: Auto-calculated Values */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Total Values
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.totalValues || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Value Sales Excluding ST
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.valueSalesExcludingST || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Sales Tax Applicable
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.salesTaxApplicable || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Fixed Notified Value/Retail Price
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.fixedNotifiedValueOrRetailPrice || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {selectedScenario && (selectedScenario.code === 'SN008' || selectedScenario.code === 'SN027') 
                                                        ? 'Calculated for ' + selectedScenario.code 
                                                        : 'Only for SN008 & SN027'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    FED Payable
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.fedPayable || ''}
                                                    onChange={(e) => handleItemChange(index, 'fedPayable', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Sales Tax Withheld at Source
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.salesTaxWithheldAtSource || ''}
                                                    onChange={(e) => handleItemChange(index, 'salesTaxWithheldAtSource', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                    placeholder="0"
                                                />
                                                <div className="text-xs text-gray-500 mt-1">Enter amount if applicable</div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Extra Tax
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.extraTax || ''}
                                                    onChange={(e) => handleItemChange(index, 'extraTax', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                    placeholder="0"
                                                />
                                                <div className="text-xs text-gray-500 mt-1">Enter amount if applicable</div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Further Tax
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.furtherTax || ''}
                                                    onChange={(e) => handleItemChange(index, 'furtherTax', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                    placeholder="0"
                                                />
                                                <div className="text-xs text-gray-500 mt-1">Enter amount if applicable</div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Discount
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.discount || ''}
                                                    onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                />
                                                <div className="text-xs text-gray-500 mt-1">Default: 0</div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Scenario-specific Fields */}
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Scenario-specific Values (Auto-filled from Scenario)</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    SRO Schedule No.
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.sroScheduleNo || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Sale Type
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.saleType || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    SRO Item Serial No.
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.sroItemSerialNo || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Tax Rate (Text)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.taxRateText || ''}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2">
                                            💡 These values are automatically populated from the selected scenario
                                        </div>
                                    </div>

                                    {/* Calculate Button */}
                                    <div className="flex justify-center mt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updatedItem = calculateItemValues(item, formData.scenarioId);
                                                handleItemChange(index, 'calculatedUpdate', updatedItem);
                                            }}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                                        >
                                            🧮 Calculate Values
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {/* Add Item Button */}
                            <div className="flex justify-center mt-6">
                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Add Item
                                </button>
                            </div>
                        </div>
                        </fieldset>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center space-x-4 py-6">
                        <button
                            type="button"
                            onClick={validateInvoice}
                            disabled={loading || !selectedScenario || !licenseStatus?.canSubmitInvoices}
                            className={`px-6 py-2 rounded-md transition-colors ${
                                !licenseStatus?.canSubmitInvoices || !selectedScenario
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
                            }`}
                            title={!licenseStatus?.canSubmitInvoices ? 'License required for FBR validation' : !selectedScenario ? 'Please select a scenario first' : ''}
                        >
                            {loading ? 'Validating...' : 'Validate with FBR'}
                        </button>
                        <button
                            type="button"
                            onClick={submitInvoice}
                            disabled={loading || !validationResult?.validation?.success || !licenseStatus?.canSubmitInvoices}
                            className={`px-6 py-2 rounded-md transition-colors ${
                                !licenseStatus?.canSubmitInvoices || !validationResult?.validation?.success
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500'
                            }`}
                            title={!licenseStatus?.canSubmitInvoices ? 'License required for FBR submission' : !validationResult?.validation?.success ? 'Please validate the invoice first' : ''}
                        >
                            {loading ? 'Submitting...' : 'Submit to FBR'}
                        </button>
                        <button
                            type="button"
                            onClick={generateInvoiceJson}
                            className="px-6 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                            View JSON
                        </button>
                    </div>
                </form>

                {/* Error Display */}
                {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <div className="text-red-400 mr-3">❌</div>
                            <div>
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="text-sm text-red-700 mt-1">{error}</p>
                            </div>
                        </div>
                    </div>
                )}



                {/* Validation Result Display */}
                {validationResult && (
                    <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-4">FBR Validation Result</h3>
                        <div className={`p-4 rounded-lg ${validationResult.validation?.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-center mb-3">
                                <div className={`flex-shrink-0 ${validationResult.validation?.success ? 'text-green-400' : 'text-red-400'}`}>
                                    {validationResult.validation?.success ? '✅' : '❌'}
                                </div>
                                <div className="ml-3">
                                    <h3 className={`text-sm font-medium ${validationResult.validation?.success ? 'text-green-800' : 'text-red-800'}`}>
                                        {validationResult.validation?.success ? 'Validation Successful!' : 'Validation Failed'}
                                    </h3>
                                </div>
                            </div>

                            {validationResult.validation?.success ? (
                                <div className="text-green-700">
                                    <p className="text-sm">Your invoice has passed all FBR validation checks and is ready for submission.</p>
                                </div>
                            ) : (
                                <div className="text-red-700 space-y-3">
                                    <div>
                                        <p className="text-sm font-medium">Error Details:</p>
                                        <div className="mt-2 p-3 bg-red-100 rounded border border-red-200">
                                            <p className="text-sm font-mono">
                                                {validationResult.validation?.error || validationResult.error || 'Validation failed'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Show full API response if available */}
                                    {validationResult.validation?.response && (
                                        <div>
                                            <p className="text-sm font-medium">FBR API Response:</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* JSON Display Modal - Only shows data that will be sent to FBR */}
            {showJsonModal && generatedJson && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-900">
                                        Invoice JSON for FBR Submission
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        This is the exact data that will be sent to FBR for validation or submission
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowJsonModal(false);
                                    setGeneratedJson(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                            <div className="space-y-4">
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <pre className="text-xs md:text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto font-mono">
                                        {JSON.stringify(generatedJson, null, 2)}
                                    </pre>
                                </div>

                                {/* Quick Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <p className="text-gray-600 font-medium">Scenario ID</p>
                                        <p className="text-blue-700 font-mono">{generatedJson.scenarioId}</p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                        <p className="text-gray-600 font-medium">Number of Items</p>
                                        <p className="text-green-700 font-mono">{generatedJson.items?.length || 0}</p>
                                    </div>
                                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                        <p className="text-gray-600 font-medium">Invoice Date</p>
                                        <p className="text-purple-700 font-mono">{generatedJson.invoiceDate}</p>
                                    </div>
                                </div>

                                {/* Reason for Debit Note */}
                                {generatedJson.invoiceType === 'Debit Note' && generatedJson.reason && (
                                    <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                        <p className="text-gray-600 font-medium">Reason for Debit Note</p>
                                        <p className="text-red-700 font-mono">{generatedJson.reason}</p>
                                    </div>
                                )}

                                {/* Buyer Info Summary */}
                                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                                    <h4 className="text-sm font-semibold text-amber-900 mb-2">📋 Buyer Information</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-amber-800">
                                        <div><strong>Name:</strong> {generatedJson.buyerBusinessName}</div>
                                        <div><strong>Type:</strong> {generatedJson.buyerRegistrationType}</div>
                                        <div><strong>NTN/CNIC:</strong> {generatedJson.buyerNTNCNIC}</div>
                                        <div><strong>Province:</strong> {generatedJson.buyerProvince}</div>
                                    </div>
                                </div>

                                {/* Copy JSON Button Info */}
                                <div className="bg-gray-100 p-3 rounded-lg border border-gray-300 text-sm text-gray-700">
                                    💡 <strong>Tip:</strong> You can copy the JSON above and use it with the FBR API endpoints for validation and submission. 
                                    All sensitive/form-only fields have been removed. This is exactly what FBR will receive.
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <button
                                onClick={() => {
                                    const jsonString = JSON.stringify(generatedJson, null, 2);
                                    navigator.clipboard.writeText(jsonString);
                                    toast.success('JSON copied to clipboard!');
                                }}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                            >
                                📋 Copy JSON
                            </button>
                            <button
                                onClick={() => {
                                    setShowJsonModal(false);
                                    setGeneratedJson(null);
                                }}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            
            {/* 🔴 AGGRESSIVE ENDPOINT CONFIRMATION MODAL - REMOVED */}
            {/* {showEndpointConfirmation && endpointInfo && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full border-4 border-red-500">
                        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 border-b-2 border-red-800">
                            <h2 className="text-2xl font-bold text-white flex items-center">
                                🚨 <span className="ml-2">FBR Endpoint Confirmation</span>
                            </h2>
                            <p className="text-red-100 text-sm mt-1">Verify the endpoint before {endpointInfo.operation === 'validate' ? 'validation' : 'submission'}</p>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className={endpointInfo.environment === 'production' ? 'p-4 rounded-lg border-2 bg-green-50 border-green-500' : 'p-4 rounded-lg border-2 bg-yellow-50 border-yellow-500'}>
                                <div className="flex items-center mb-2">
                                    <span className="text-2xl mr-2">{endpointInfo.environment === 'production' ? '✅' : '⚠️'}</span>
                                    <h3 className={endpointInfo.environment === 'production' ? 'text-lg font-bold text-green-800' : 'text-lg font-bold text-yellow-800'}>
                                        Environment: <span className="uppercase font-mono">{endpointInfo.environment}</span>
                                    </h3>
                                </div>
                                <p className={endpointInfo.environment === 'production' ? 'text-sm text-green-700' : 'text-sm text-yellow-700'}>
                                    Your account is configured to use the <strong>{endpointInfo.environment}</strong> environment.
                                </p>
                            </div>

                            <div className="bg-gray-900 text-green-400 p-4 rounded-lg border-2 border-green-400 font-mono text-sm space-y-3">
                                <div>
                                    <span className="text-gray-500"># Base URL:</span>
                                    <br />
                                    <span className="text-white">{endpointInfo.baseURL}</span>
                                </div>
                                <div className="border-t border-green-400 pt-3">
                                    <span className="text-gray-500"># Endpoint:</span>
                                    <br />
                                    <span className={endpointInfo.environment === 'production' ? 'text-xl font-bold text-cyan-300' : 'text-xl font-bold text-yellow-300'}>{endpointInfo.endpoint}</span>
                                </div>
                                <div className="border-t border-green-400 pt-3">
                                    <span className="text-gray-500"># Full URL:</span>
                                    <br />
                                    <span className="text-blue-300 break-all text-xs">{endpointInfo.fullURL}</span>
                                </div>
                            </div>

                            {endpointInfo.environment === 'sandbox' && (
                                <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                                    <h4 className="font-bold text-yellow-800 flex items-center">
                                        ⚠️ Sandbox Mode
                                    </h4>
                                    <p className="text-yellow-700 text-sm mt-2">
                                        You are about to {endpointInfo.operation} an invoice in <strong>SANDBOX</strong>. The invoice will NOT appear in your production FBR account. 
                                        The FBR invoice number will include a <code className="bg-yellow-200 px-2 py-1 rounded">-1</code> suffix.
                                    </p>
                                </div>
                            )}

                            {endpointInfo.environment === 'production' && (
                                <div className="p-4 bg-green-100 border-l-4 border-green-500 rounded">
                                    <h4 className="font-bold text-green-800 flex items-center">
                                        ✅ Production Mode
                                    </h4>
                                    <p className="text-green-700 text-sm mt-2">
                                        You are about to {endpointInfo.operation} an invoice in <strong>PRODUCTION</strong>. The invoice WILL appear in your FBR production account.
                                        The invoice number will <strong>NOT</strong> have a "-1" suffix.
                                    </p>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <strong>Operation:</strong> {endpointInfo.operation === 'validate' ? '🔍 Validation' : '🚀 Submission'}
                                </p>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t-2 border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowEndpointConfirmation(false);
                                    setPendingOperation(null);
                                    setEndpointInfo(null);
                                }}
                                className="px-6 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors font-semibold"
                            >
                                ❌ Cancel
                            </button>
                            <button
                                onClick={() => proceedWithConfirmedEndpoint(endpointInfo.operation)}
                                className={endpointInfo.environment === 'production' ? 'px-6 py-3 text-white rounded-lg transition-colors font-semibold bg-green-600 hover:bg-green-700' : 'px-6 py-3 text-white rounded-lg transition-colors font-semibold bg-yellow-600 hover:bg-yellow-700'}
                            >
                                ✅ Yes, {endpointInfo.operation === 'validate' ? 'Validate' : 'Submit'} to {endpointInfo.environment.toUpperCase()}
                            </button>
                        </div>
                    </div>
                </div>
            )} */}

            {/* Buyer Management Modal */}
            <BuyerModal 
                isOpen={showBuyerPopup} 
                onClose={() => setShowBuyerPopup(false)}
                onSelectBuyer={(buyer) => selectBuyer(buyer)}
            />
        </div>
    );
      
  }; // This '}' now correctly closes the CanvasInvoiceForm component

 export default CanvasInvoiceForm;
